import React, { useEffect } from "react";

import { wrap } from "./RydTestbedWrap";
import {
    RydTrack,
    RydTrackUIState
} from "../model/tracks";
import { getDefaulAudioSource } from "../audio/routing";
import {
    listVirtualInstruments,
    RydSFInstrument
} from "../model/instrument";
import PianoRoll from "../components/rolls/PianoRoll";
import {
    __DEFAULT_NOTE_RANGES,
    MetronomeMarking,
    NoteDuration
} from "../model/core";
import { useAudioContextStore } from "../stores/AudioContextStore";
import * as ToneJS from "tone"
import { getAudioContext } from "../audio/AudioController";
import { useMixerStore } from "../stores/MixerStore";
import { RydMixer } from "../audio/mixer";
import { RydAudioMixer } from "../components/audio/RydAudioMixer";
import { TrackColor } from "../render/trackColor";
import { ComputedContextFrame } from "../model/context";
import { HorizontalNoteContainer } from "../model/containers";
import { RydProject } from "../model/project";


const CONTEXT_FRAME: ComputedContextFrame = {
    timeSignature: { numerator: 4, denominator: 4 },
    metronome: new MetronomeMarking(50, new NoteDuration(1)),
}

type TrackChannel = {
    track: RydTrack,
    trackColor?: TrackColor.Color,
    channel: RydMixer.TrackParams
};

export function MixerPanelTestbed() {

    const [initialized, setInitialized] = React.useState(false);

    const audioContextStore = useAudioContextStore();
    const [tracks, setTracks] = React.useState<TrackChannel[]>();
    const mixerStore = useMixerStore();

    useEffect(() => {
        if (!initialized) return;
        console.log('Initializing audio context...', audioContextStore);
        const audioContext = getAudioContext();
        if (!audioContextStore.audioContext) {
            console.log('Creating new AudioContext:', audioContext);
            audioContextStore.setAudioContext(audioContext);
            ToneJS.setContext(audioContext);
            ToneJS.start();
        }
        const DUMMY_HNC = new HorizontalNoteContainer({ length: 32 });
        const TRACKS: RydTrack[] = [
            {
                id: 1,
                name: "Guitar",
                instrument: listVirtualInstruments()[0].create(audioContext),
                inputSource: getDefaulAudioSource(),
                length: 32,
                color: TrackColor.getRandomColor(),
                sequence: DUMMY_HNC,
                bookmarks: [],
            },{
                id: 2,
                name: "Guitar",
                instrument: listVirtualInstruments()[1].create(audioContext),
                inputSource: getDefaulAudioSource(),
                length: 32,
                color: TrackColor.getRandomColor(),
                sequence: DUMMY_HNC,
                bookmarks: [],
            }, {
                id: 3,
                name: "Piano",
                instrument: new RydSFInstrument('piano', 'piano', 'acoustic_grand_piano', audioContext),
                inputSource: getDefaulAudioSource(),
                length: 32,
                color: TrackColor.getRandomColor(),
                sequence: DUMMY_HNC,
                bookmarks: [],
            },
        ];

        TRACKS.forEach(track => {
            track.sequence.setLength(32); // Set each track to a length of 32 beats

        });

        setTracks(TRACKS.map(t => {
            return {
                track: t,
                channel: {
                    id: t.id,
                    name: t.name,
                    gain: 1.0,
                    pan: 0.0,
                    muted: false,
                    solo: false,
                    sourceNode: t.instrument?.getOutputNode(),
                    onBus: true
                },
                trackColor: TrackColor.DEFAULT_COLOR
            };
        }));

    }, [initialized]);

    useEffect(() => {
        if (!audioContextStore.audioContext) return;
        if (!mixerStore.mixer) {
            mixerStore.setMixer(new RydMixer.Mixer(audioContextStore.audioContext));
        }
    }, [mixerStore, audioContextStore]);

    if (!initialized || !audioContextStore.audioContext) {
        return wrap(<button onClick={() => {
            console.log('Still initializing audio context...');
            setInitialized(true);
        }}>Initialize audio</button>);
    }

    if (!tracks) {
        return wrap(<div>Loading tracks...</div>);
    }

    return wrap(<>
        {/* Add your testbed components here */}
        <h1>MixerPanel Testbed</h1>

        <RydTrackList
            tracks={tracks.map(t => t.track)}
            setTracks={(t) => {
                console.log('Setting tracks:', t);
            }}
        />
        <RydAudioMixer
            tracks={tracks.map(t => {
                return {
                    ...t.channel,
                    trackColor: t.track.color as TrackColor.Color
                }
            })}
            onUpdate={(id, changes) => {
                const track = tracks.find(t => t.track.id === id);
                if (track) {
                    Object.assign(track.channel, changes);
                }
                console.log('Updated track:', id, changes);
                setTracks([...tracks]); // Trigger re-render
                console.log('tracks after update:', tracks);
            }}
            onUpdateMaster={(changes) => {
                console.log( 'Updating master track:', changes);
            }}
        // isCollapsed={false}
        // toggleCollapse={() => console.log('Toggle mixer collapse')}
        />

    </>);
}

function RydTrackList({
    tracks,
    setTracks
}: {
    tracks: RydTrack[],
    setTracks: (tracks: RydTrack[]) => void
}) {

    const [uiState, setUiState] = React.useState<RydTrackUIState[]>(tracks.map((t) => {
        return {
            trackId: t.id,
            collapsed: true,
            noteRange: __DEFAULT_NOTE_RANGES['notes'],
            tonalityVisible: false,
            color: TrackColor.DEFAULT_COLOR,
            gridResolution: 'bar',
            snapToGrid: true,
            editMode: 'NOTES',
        };
    }));

    return (
        <div>
            <h2>Track List</h2>
            <ul>
                {tracks.map((track, i) => (
                    <li key={track.id}>
                        {track.name} - {track.instrument?.name} ({track.inputSource?.getdisplayName()})
                        <PianoRoll
                            project={RydProject.createForNewProject('Test Project')}
                            track={track}
                            setTrack={(newTrack) => {
                                const newTracks = [...tracks];
                                newTracks[i] = newTrack;
                                setTracks(newTracks);
                            }}
                            hnc={track.sequence}
                            trackInstrument={track.instrument}
                            contextFrame={CONTEXT_FRAME}
                            readonly={false}
                            uiState={uiState[i]}
                            setTrackUIState={(newUiState) => {
                                const newUiStateCopy = [...uiState];
                                newUiStateCopy[i] = newUiState;
                                setUiState(newUiStateCopy);
                            }}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}